import type { LeadGeneratorAgentPayload } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/lead-generator/types/dto";
import type { Competitor } from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";

const AI_AGENT_COMPANY_INFO_QUERY_KEY = "ai-agent:company-info";
const AI_AGENT_COMPETITORS_QUERY_KEY = "ai-agent:competitors";
const AI_AGENT_APPLY_COMPETITORS_QUERY_KEY = "ai-agent:apply-competitors";
const AI_AGENT_PRODUCT_OFFERINGS_QUERY_KEY = "ai-agent:product-offerings";
const AI_AGENT_USER_INFO_QUERY_KEY = "ai-agent:user-info";
const AI_AGENT_THREAD_QUERY_KEY = "ai-agent:thread";
const AI_AGENT_CHAT_QUERY_KEY = "ai-agent:chat";
const AI_AGENT_THREADS_QUERY_KEY = "ai-agent:threads-history";
const AI_AGENT_CONTACTS_QUERY_KEY = "ai-agent:get-contacts";

const AI_AGENT_EMAIL_THREAD_QUERY_KEY = "ai-agent:thread-email";
const AI_AGENT_EMAIL_THREAD_BY_ID_QUERY_KEY = "ai-agent:thread-email-by-id";

// Lead Gen Agent specific keys
const BASE_LEAD_GEN_AGENT_QUERY_KEY = "ai-agent:lead-gen";
const LEAD_GEN_AGENT_THREAD_QUERY_KEY = `${BASE_LEAD_GEN_AGENT_QUERY_KEY}:thread`;
const LEAD_GEN_AGENT_CHAT_QUERY_KEY = `${BASE_LEAD_GEN_AGENT_QUERY_KEY}:chat`;

// Super Agent specific keys
const BASE_SUPER_AGENT_QUERY_KEY = "super-agent";
const SUPER_AGENT_CHAT_QUERY_KEY = `${BASE_SUPER_AGENT_QUERY_KEY}:chat`;
const SUPER_AGENT_THREAD_QUERY_KEY = `${BASE_SUPER_AGENT_QUERY_KEY}:thread`;
const SUPER_AGENT_RECENTS_QUERY_KEY = `${BASE_SUPER_AGENT_QUERY_KEY}:recents`;
const SUPER_AGENT_CONTACTS_QUERY_KEY = `${BASE_SUPER_AGENT_QUERY_KEY}:contacts`;
const SUPER_AGENT_EMAIL_THREAD_QUERY_KEY = `${BASE_SUPER_AGENT_QUERY_KEY}:thread-email`;

type LeadGeneratorChatKey = readonly (string | Record<string, unknown>)[];

type AiAgentQueryKeys = {
    companyInfo: (domain: string) => readonly string[];
    competitors: (domain: string) => readonly string[];
    productOfferings: () => readonly string[];
    userInfo: () => readonly string[];
    generateThread: () => readonly string[];
    generateEmailThread: (domain: string, recipient: string, isCompanyLevel: boolean) => readonly string[];
    generateEmailThreadForSuperAgent: (
        domain: string,
        recipient: string,
        isCompanyLevel: boolean,
        messageId: string,
    ) => readonly string[];
    threadEmailById: (threadId: string) => readonly string[];
    applyEmailCompetitors: (domain?: string, competitors?: Competitor[]) => readonly string[];
    threadById: (id: string) => readonly string[];
    chat: (questionId: string) => readonly string[];
    threads: () => readonly string[];
    contacts: (domain: string, search?: string, limit?: number) => readonly string[];
    contactsAll: () => readonly string[];

    leadGenerator: {
        thread: (savedSearchId?: string | null) => readonly string[];
        chat: (threadId: string, messageId?: number, data?: LeadGeneratorAgentPayload) => LeadGeneratorChatKey;
    };

    superAgent: {
        chat: (questionId: string) => readonly string[];
        recents: () => readonly string[];
        threadById: (threadId: string) => readonly string[];
        contacts: (ids: string[]) => readonly string[];
    };
};

export const aiAgentQueryKeys: AiAgentQueryKeys = {
    companyInfo: (domain): readonly string[] => [AI_AGENT_COMPANY_INFO_QUERY_KEY, domain],
    competitors: (domain): readonly string[] => [AI_AGENT_COMPETITORS_QUERY_KEY, domain],
    productOfferings: (): readonly string[] => [AI_AGENT_PRODUCT_OFFERINGS_QUERY_KEY],
    userInfo: (): readonly string[] => [AI_AGENT_USER_INFO_QUERY_KEY],
    generateThread: (): readonly string[] => [AI_AGENT_THREAD_QUERY_KEY],
    generateEmailThread: (domain, recipient, isCompanyLevel): readonly string[] => [
        AI_AGENT_EMAIL_THREAD_QUERY_KEY,
        domain,
        recipient,
        isCompanyLevel.toString(),
    ],
    generateEmailThreadForSuperAgent: (domain, recipient, isCompanyLevel, messageId): readonly string[] => [
        SUPER_AGENT_EMAIL_THREAD_QUERY_KEY,
        domain,
        recipient,
        isCompanyLevel.toString(),
        messageId,
    ],
    threadEmailById: (threadId): readonly string[] => [AI_AGENT_EMAIL_THREAD_BY_ID_QUERY_KEY, threadId],
    applyEmailCompetitors: (domain, competitors): readonly string[] => [
        AI_AGENT_APPLY_COMPETITORS_QUERY_KEY,
        domain || "",
        competitors?.map((c) => c.domain).join(",") || "",
    ],
    threadById: (id): readonly string[] => [AI_AGENT_THREAD_QUERY_KEY, id],
    chat: (questionId): readonly string[] => [AI_AGENT_CHAT_QUERY_KEY, questionId],
    threads: (): readonly string[] => [AI_AGENT_THREADS_QUERY_KEY],
    contacts: (domain, search, limit): readonly string[] => [
        AI_AGENT_CONTACTS_QUERY_KEY,
        domain,
        search ?? "",
        limit?.toString() ?? "",
    ],
    contactsAll: (): readonly string[] => [AI_AGENT_CONTACTS_QUERY_KEY],

    leadGenerator: {
        thread: (savedSearchId?): readonly string[] =>
            [LEAD_GEN_AGENT_THREAD_QUERY_KEY, savedSearchId].filter(Boolean) as readonly string[],

        chat: (threadId, messageId, data): LeadGeneratorChatKey =>
            [LEAD_GEN_AGENT_CHAT_QUERY_KEY, threadId, messageId, data].filter(Boolean) as LeadGeneratorChatKey,
    },

    superAgent: {
        chat: (questionId): readonly string[] => [SUPER_AGENT_CHAT_QUERY_KEY, questionId],
        recents: (): readonly string[] => [SUPER_AGENT_RECENTS_QUERY_KEY],
        threadById: (threadId): readonly string[] => [SUPER_AGENT_THREAD_QUERY_KEY, threadId],
        contacts: (ids: string[]): readonly string[] => [SUPER_AGENT_CONTACTS_QUERY_KEY, ...ids],
    },
};
